import {verifyUrl} from "../../common/config/config";
import {MainConfig, Projection, RuntimeConfig} from "../../common/config/MainConfig";
import {isNumber, nnTrim, prefixUrl} from "../../common/util";
import * as LZString from "lz-string";

/**
 * A runtime config for EDID search app
 * @extends RuntimeConfig
 * @hideconstructor
 */
class EdidSearchRuntimeConfig extends RuntimeConfig {

    getClassName() {
        return "EdidSearchRuntimeConfig";
    }

    /**
     * Clean this runtime config
     */
    clean() {
        this.config = {
            e: {},
        };
    }

    /**
     * Create a EdidSearchRuntimeConfig from a serialized object
     * @param code {string} Unique identifier for the runtime config
     * @param runtimeConfigSerialization {string} serialization
     * @return {EdidSearchRuntimeConfig}
     */
    static createFromSerialized(code, runtimeConfigSerialization) {
        let parsedConfig;
        try {
            parsedConfig = runtimeConfigSerialization
                // ? JSON.parse((runtimeConfigSerialization))
                ? JSON.parse(LZString.decompressFromBase64(runtimeConfigSerialization))
                : undefined;
            // tumbo.LOGGER.logDebug("Deserialized config: ", parsedConfig);
        } catch (e) {
            tumbo.LOGGER.logError('Error deserializing config', e);
        }
        return new EdidSearchRuntimeConfig(code, parsedConfig);
    }
}


/**
 * The configuration of the EDID search
 * @extends MainConfig
 * @hideconstructor
 */
class EdidSearchConfig extends MainConfig {

    getClassName() {
        return "EdidSearchConfig";
    }

    getName() {
        return this.config.edidSearch.name;
    }

    getCode() {
        return this.config.edidSearch.code;
    }

    /**
     * List of available projections
     * @return {Array<Projection>}
     */
    getProjectionConfigs() {
        let projs = [];
        if (this.config.projections) {
            for (let p in this.config.projections) {
                projs.push(new Projection(this.config.projections[p]))
            }
        }
        return projs;
    }

    _getStatsPostUrlProperty() {
        return this.config.edidSearch.statsPostUrl;
    }

    _getContactPostUrlProperty() {
        return this.config.edidSearch.contactPostUrl;
    }

    _getHelpContentsUrlProperty() {
        return this.config.edidSearch.helpContentsUrl;
    }

    /**
     *
     * @return {Projection}
     */
    getProjection() {
        const projectionCode = this.getProjectionCode();
        const proj = projectionCode ? this.getProjectionConfigs().filter(p => p.getCode() === projectionCode) [0] : undefined;
        return proj ? proj : Projection.WGS84_PROJECTION;
    }

    getProjectionCode() {
        return nnTrim(this.config.edidSearch.projection);
    }


    getFirstYear() {
        return this.config.edidSearch.firstYear;
    }

    getLastYear() {
        return this.config.edidSearch.lastYear;
    }


    getRecordsByIdServiceUrl() {
        return prefixUrl(this.config.edidSearch.recordsByIdService);
    }

    getSearchRecordsServiceUrl() {
        return prefixUrl(this.config.edidSearch.searchRecordsService);
    }

    getRegionsServiceUrl() {
        return prefixUrl(this.config.edidSearch.regionsService);
    }

    getTableContextInfo() {
        return nnTrim(this.config.edidSearch.tableContextInfo);
    }

    /**
     * Override this config
     * @param runtimeConfig {EdidSearchRuntimeConfig} The configuration override
     */
    override(runtimeConfig) {

    }

    /**
     * Verify this configuration and return a list of errors
     * @return {Array<string>}
     */
    verify() {
        let errors = super.verify();

        const projectionConfigs = this.getProjectionConfigs();
        if (projectionConfigs.length === 0) {
            errors.push(this.getClassName() + ': projections is mandatory');
        } else {
            for (let p in projectionConfigs) {
                errors = errors.concat(projectionConfigs[p].verify());
            }
        }

        const firstYear = this.getFirstYear();
        if (!firstYear) {
            errors.push(this.getClassName() + ': edidSearch.firstYear is mandatory');
        } else if (!isNumber(firstYear) || firstYear < 1900 || firstYear > new Date().getFullYear()) {
            errors.push(this.getClassName() + ': edidSearch.firstYear is invalid');
        }
        const lastYear = this.getFirstYear();
        if (!lastYear) {
            errors.push(this.getClassName() + ': edidSearch.lastYear is mandatory');
        } else if (!isNumber(lastYear) || lastYear < 1900 || lastYear > new Date().getFullYear()) {
            errors.push(this.getClassName() + ': edidSearch.lastYear is invalid');
        }


        const recordsByIdServiceUrl = this.getRecordsByIdServiceUrl();
        if (recordsByIdServiceUrl) {
            if (!verifyUrl(recordsByIdServiceUrl)) {
                errors.push(this.getClassName() + ': invalid edidSearch.recordsByIdService URL');
            }
        }

        const searchRecordsServiceUrl = this.getSearchRecordsServiceUrl();
        if (searchRecordsServiceUrl) {
            if (!verifyUrl(searchRecordsServiceUrl)) {
                errors.push(this.getClassName() + ': invalid edidSearch.searchRecordsService URL');
            }
        }

        const regionsServiceUrl = this.getRegionsServiceUrl();
        if (regionsServiceUrl) {
            if (!verifyUrl(regionsServiceUrl)) {
                errors.push(this.getClassName() + ': invalid edidSearch.regionsService URL');
            }
        }
        return errors;
    }
}


export {EdidSearchConfig, EdidSearchRuntimeConfig}